import { useLang } from '../i18n/lang';
import type { Strings } from '../i18n/strings';
import type { Trail, Standpoint } from './trail';

/*
 * WHAT THE MAP HAS NOTICED ABOUT HOW YOU READ, and where you have been.
 *
 * Two things a reader cannot see any other way. The posture is INFERRED — the design's five
 * self-declared options exist in its copy and are deliberately not offered as a choice, because
 * asking somebody to pick a label before they have read anything is asking them to perform an
 * identity rather than have one. So this reports, in the second person, what it noticed, and says
 * plainly what it will do differently as a result: a posture inferred and never acted on would be
 * a horoscope.
 *
 * And the trail record — the nearest you have come to the divine and the furthest out — which is
 * the only place the map's central claim becomes a fact about YOU rather than about the field.
 *
 * Collapsed by default and a real `<details>`, exactly as the legend is: keyboard-reachable, no
 * state of its own, and never covering the map until it is asked for.
 */

/*
 * THE EFFECT LINES ARE WRITTEN AND ARE NOT SHOWN. They said behaviour that does not happen.
 *
 * "Shared names and doubled stories surface first", "disputed identities stay unsmoothed", "the
 * original sits above the English" — each names a real change to ranking or panel order, and none
 * of them happens today. The card truthfully reported what the map had NOTICED and then stated an
 * intent it does not act on, which is the exact shape this file's own comment calls a horoscope.
 *
 * The standing note said it plainly: until they are wired, either they are wired or the lines come
 * out. They are out. The strings and the `SAID` mapping stay, so landing them in step 5 is putting
 * one line back rather than writing three sentences again in three languages.
 *
 * IT IS ALSO WHAT THEIRS SHOWS. Its card is an eyebrow, a name and one sentence — "READING AS / the
 * newcomer / You walked without a map and did not ask for one" — and no claim about what the map
 * will do differently. The line that had to go and the line theirs does not have are the same line.
 */

/** Each posture's three phrases, so a lookup cannot half-exist. */
const SAID: Record<Standpoint, { name: keyof Strings; why: keyof Strings; effect: keyof Strings }> = {
  comparatist: { name: 'standpoint.comparatist', why: 'standpoint.comparatist.why', effect: 'standpoint.comparatist.effect' },
  sceptic: { name: 'standpoint.sceptic', why: 'standpoint.sceptic.why', effect: 'standpoint.sceptic.effect' },
  originals: { name: 'standpoint.originals', why: 'standpoint.originals.why', effect: 'standpoint.originals.effect' },
  newcomer: { name: 'standpoint.newcomer', why: 'standpoint.newcomer.why', effect: 'standpoint.newcomer.effect' },
};

export interface MapStandpointProps {
  trail: Trail;
  standpoint: Standpoint;
  /** The nearest and furthest, already looked up in TODAY's map — see `trail.record`. */
  record: { closestIn: string | null; furthestOut: string | null };
  /** What to call a figure, and what rung it stands on. Both asked of the current build. */
  nameOf(id: string): string | null;
  rungName(id: string): string | null;
  /**
   * HOW MUCH OF EACH SCRIPTURE THEY HAVE ACTUALLY SEEN — their YOUR ATLAS (universe-v8.dc.html:180).
   *
   * The one block of their reader's menu we had no answer to, and the only place the map says how
   * far a reader has got rather than where they have been. `coverage` in `map/v8/walk.ts` has been
   * ported and checked against their own method for weeks; nothing ever displayed it.
   *
   * Rows are pre-resolved to a name and a colour by the caller, because this component does no
   * lookups — the same rule the panel's NAMED IN list follows.
   */
  atlas?: readonly { key: string; name: string; colour: string; got: number; tot: number; pct: number }[];
}

export function MapStandpoint({ trail, standpoint, record, nameOf, rungName, atlas }: MapStandpointProps): React.JSX.Element {
  const { t } = useLang();
  const said = SAID[standpoint];
  const walked = trail.visited.length > 0;

  return (
    /*
     * NOT ITS OWN DISCLOSURE ANY MORE. This was a `<details>` inside the wordmark's menu, shut, so
     * the atlas — the only place the map reports how far a reader has got — was two clicks away.
     *
     * It opened by default first, which was better and still wrong: theirs hangs this off a control
     * of its own in the top-right corner, beside the count of who else is here, and that control IS
     * the fold. A fold inside a fold inside a menu was two more clicks than the thing deserves.
     *
     * So the card is a card, and `MapYou` opens it.
     */
    <section className="standpoint" aria-label={t('you.open')}>
      <div className="standpoint-body">
        <p className="standpoint-eyebrow">{t('you.readingAs')}</p>
        <p className="standpoint-name">{t(said.name)}</p>
        {/* The evidence, then what it changes. Never one without the other. */}
        <p className="standpoint-why">{t(said.why, { texts: trail.corpora.length })}</p>
        {/* `said.effect` is deliberately not rendered — see the note at the top of this file. */}

        {walked ? (
          <dl className="standpoint-record">
            <Reach label={t('you.closestIn')} id={record.closestIn} nameOf={nameOf} rungName={rungName} />
            <Reach label={t('you.furthestOut')} id={record.furthestOut} nameOf={nameOf} rungName={rungName} />
          </dl>
        ) : (
          /* Said plainly rather than shown as two empty slots — a record of nothing is not a record. */
          <p className="standpoint-nowhere">{t('you.nowhere')}</p>
        )}

        {atlas !== undefined && atlas.length > 0 && (
          <div className="standpoint-atlas">
            <p className="standpoint-eyebrow">{t('you.atlas')}</p>
            <ul className="standpoint-atlas-rows">
              {atlas.map(row => (
                <li key={row.key}>
                  <span className="standpoint-atlas-dot" style={{ backgroundColor: row.colour }} aria-hidden="true" />
                  <span className="standpoint-atlas-name">{row.name}</span>
                  {/* The bar carries no text of its own: the count beside it already says the number,
                      and a reader using a screen reader gets that rather than a length in pixels. */}
                  <span className="standpoint-atlas-bar" aria-hidden="true">
                    <span style={{ width: `${Math.round(row.pct * 100)}%`, backgroundColor: row.colour }} />
                  </span>
                  <span className="standpoint-atlas-count">{t('you.atlasRow', { got: row.got, tot: row.tot })}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* The line that makes everything above it safe to have said: none of this is a claim about
            belief, and nothing here was asked. It used to close the arrival's full-screen reveal;
            with that gone it belongs wherever the inference is shown, which is here. */}
        <p className="standpoint-never">{t('standpoint.neverAsked')}</p>
      </div>
    </section>
  );
}

/**
 * One end of the trail: a name, over the rung it stands on TODAY.
 *
 * Both looked up rather than remembered. The stored trail holds ids and no rung at all, because
 * the rungs are editorial and a rebuild can move a figure — a remembered rung would have this
 * insisting the reader had been somewhere the map no longer says exists.
 */
function Reach({ label, id, nameOf, rungName }: {
  label: string;
  id: string | null;
  nameOf(id: string): string | null;
  rungName(id: string): string | null;
}): React.JSX.Element | null {
  /* A figure this build no longer carries is dropped rather than shown as a bare id. */
  const name = id === null ? null : nameOf(id);
  if (name === null) return null;
  return (
    <>
      <dt className="standpoint-reach-label">{label}</dt>
      <dd className="standpoint-reach">
        <span className="standpoint-reach-name">{name}</span>
        <span className="standpoint-reach-rung">{id === null ? '' : rungName(id) ?? ''}</span>
      </dd>
    </>
  );
}
